import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest, isAbortError } from "./apiClient";

/* One place where every admin list handles loading, errors and cancellation.
 *
 * Previously two of the five fetchers had no try/catch at all and the rest
 * swallowed failures into an empty array, so each list behaved differently on
 * a bad connection. This makes that behaviour uniform and cancels the request
 * on unmount, so nothing sets state on a component that is already gone. */
export function useCollection(path, select) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });

  // Kept in a ref so an inline arrow doesn't re-trigger the effect every render.
  const selectRef = useRef(select);
  useEffect(() => {
    selectRef.current = select;
  });

  const controllerRef = useRef(null);

  const load = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setState((current) => ({ ...current, status: "loading", error: null }));

    try {
      const raw = await apiRequest(path, { signal: controller.signal });
      setState({ status: "ready", data: selectRef.current(raw), error: null });
    } catch (err) {
      if (isAbortError(err)) return;
      setState({ status: "error", data: null, error: err.message });
    }
  }, [path]);

  useEffect(() => {
    load();
    return () => controllerRef.current?.abort();
  }, [load]);

  const setData = useCallback((updater) => {
    setState((current) => ({
      ...current,
      data: typeof updater === "function" ? updater(current.data) : updater,
    }));
  }, []);

  return { ...state, reload: load, setData };
}
